import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, listAdminPayments, getAdminUserAvatar } from '../lib/api';
import { openExternal } from '../lib/tg';
import { useStore } from '../lib/store';
import { useDebounce } from '../lib/useDebounce';
import type {
  AdminDebug,
  AdminPayment,
  AdminPaymentsResponse,
  AdminUser,
  AdminUsersResponse,
  MeResponse,
} from '../lib/types';

type Section = 'debug' | 'users' | 'payments';

function fmtBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}д ${h}ч ${m}м`;
  if (h > 0) return `${h}ч ${m}м`;
  return `${m}м ${Math.floor(sec % 60)}с`;
}

function fmtAge(ms: number | null | undefined): string {
  if (!ms) return 'никогда';
  const sec = Math.floor((Date.now() - ms) / 1000);
  if (sec < 60) return `${sec}с назад`;
  if (sec < 3600) return `${Math.floor(sec / 60)}м назад`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}ч ${Math.floor((sec % 3600) / 60)}м назад`;
  return `${Math.floor(sec / 86400)}д назад`;
}

function fmtDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('ru-RU');
}

// Activity recency → dot colour + label. Green ≤24h, amber ≤7d, dim older,
// red when the user has never been seen.
function seenInfo(ms: number | null | undefined): { color: string; label: string } {
  if (!ms) return { color: 'var(--neon-red)', label: 'не заходил' };
  const age = Date.now() - ms;
  if (age < 86_400_000) return { color: 'var(--neon-green)', label: fmtAge(ms) };
  if (age < 7 * 86_400_000) return { color: 'var(--neon-yellow)', label: fmtAge(ms) };
  return { color: 'var(--text-dim)', label: fmtAge(ms) };
}

function DebugSection(): JSX.Element {
  const q = useQuery<AdminDebug>({
    queryKey: ['admin-debug'],
    queryFn: () => api<AdminDebug>('/v1/admin/debug'),
    refetchInterval: 30_000,
  });

  if (q.isLoading) return <div className="text-sm text-text-dim">Загрузка…</div>;
  if (q.isError) return <div className="text-sm text-neon-red">{(q.error as Error).message}</div>;
  if (!q.data) return <></>;

  const d = q.data;
  return (
    <div className="space-y-4 text-sm">
      <div className="rounded-md border border-white/5 bg-bg-elev p-3">
        <div className="mb-2 text-xs uppercase tracking-wide text-text-dim">Процесс</div>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
          <dt className="text-text-dim">Uptime</dt>
          <dd>{fmtUptime(d.process.uptimeSec)}</dd>
          <dt className="text-text-dim">Node</dt>
          <dd className="font-mono">{d.process.node}</dd>
          <dt className="text-text-dim">RSS</dt>
          <dd>{fmtBytes(d.process.memRss)}</dd>
          <dt className="text-text-dim">Heap</dt>
          <dd>
            {fmtBytes(d.process.memHeapUsed)} / {fmtBytes(d.process.memHeapTotal)}
          </dd>
          <dt className="text-text-dim">Proxy</dt>
          <dd className="truncate">{d.process.proxyUrl ?? '—'}</dd>
        </dl>
      </div>

      <div className="rounded-md border border-white/5 bg-bg-elev p-3">
        <div className="mb-2 text-xs uppercase tracking-wide text-text-dim">База</div>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
          <dt className="text-text-dim">Пользователи</dt>
          <dd>{d.db.users.total}</dd>
          <dt className="text-text-dim">Заблокировали бота</dt>
          <dd>{d.db.users.blocked}</dd>
        </dl>
      </div>
    </div>
  );
}

function UserAvatar({
  tgId,
  username,
}: {
  tgId: number;
  username: string | null;
}): JSX.Element {
  const [broke, setBroke] = useState(false);
  const q = useQuery({
    queryKey: ['admin-avatar', tgId],
    queryFn: () => getAdminUserAvatar(tgId),
    staleTime: 30 * 60 * 1000,
  });
  const url = broke ? null : q.data?.url ?? null;
  if (url) {
    return (
      <img
        src={url}
        alt=""
        loading="lazy"
        onError={() => setBroke(true)}
        className="h-9 w-9 shrink-0 rounded-md object-cover"
      />
    );
  }
  return (
    <div
      className="grid h-9 w-9 shrink-0 place-items-center rounded-md font-display text-sm"
      style={{
        background: 'var(--bg)',
        color: 'var(--text-dim)',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)',
      }}
    >
      {(username?.[0] ?? '#').toUpperCase()}
    </div>
  );
}

function UserRow({
  u,
  viewerIsSuperAdmin,
}: {
  u: AdminUser;
  viewerIsSuperAdmin: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  const showToast = useStore((s) => s.showToast);

  const setAdminFlag = useMutation({
    mutationFn: (next: boolean) =>
      api<{ ok: true }>(`/v1/admin/users/${u.tgId}/${next ? 'grant-admin' : 'revoke-admin'}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
      showToast('Готово');
    },
    onError: (e: Error) => showToast(e.message || 'Не удалось изменить'),
  });

  const seen = seenInfo(u.lastSeenAt);

  return (
    <div className="flex items-start gap-3 rounded-lg border border-white/5 bg-bg-elev p-3">
      <UserAvatar tgId={u.tgId} username={u.username} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate font-medium">
              {u.username ? `@${u.username}` : <span className="text-text-dim">без username</span>}
            </div>
            <div className="font-mono text-xs text-text-dim">#{u.tgId}</div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {u.isSuperAdmin && (
              <span className="rounded bg-accent/15 px-1.5 py-0.5 text-[10px] uppercase text-accent">
                super
              </span>
            )}
            {u.isAdmin && !u.isSuperAdmin && (
              <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase text-text-dim">
                admin
              </span>
            )}
            {u.blockedAt !== null && (
              <span className="rounded bg-neon-red/10 px-1.5 py-0.5 text-[10px] uppercase text-neon-red">
                blocked
              </span>
            )}
          </div>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-dim">
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: seen.color }}
            />
            {seen.label}
          </span>
          <span aria-hidden>·</span>
          <span>с {fmtDate(u.createdAt)}</span>
        </div>
        {viewerIsSuperAdmin && !u.isSuperAdmin && (
          <button
            disabled={setAdminFlag.isPending}
            onClick={() => setAdminFlag.mutate(!u.isAdmin)}
            className="mt-2 rounded border border-white/10 px-2 py-0.5 text-xs text-text-dim transition hover:bg-white/5 disabled:opacity-40"
          >
            {u.isAdmin ? 'снять админку' : 'сделать админом'}
          </button>
        )}
      </div>
    </div>
  );
}

function UsersSection({ viewerIsSuperAdmin }: { viewerIsSuperAdmin: boolean }): JSX.Element {
  const [q, setQ] = useState('');
  const debouncedQ = useDebounce(q.trim(), 300);
  const list = useQuery<AdminUsersResponse>({
    queryKey: ['admin-users', debouncedQ],
    queryFn: () =>
      api<AdminUsersResponse>(
        `/v1/admin/users?limit=100${debouncedQ ? `&q=${encodeURIComponent(debouncedQ)}` : ''}`,
      ),
  });

  return (
    <div className="space-y-2">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Поиск по нику или id"
        className="w-full rounded-md border border-white/10 bg-bg-elev px-3 py-2 text-sm outline-none focus:border-accent/50"
      />
      {list.isLoading && <div className="text-sm text-text-dim">Загрузка…</div>}
      {list.isError && (
        <div className="text-sm text-neon-red">{(list.error as Error).message}</div>
      )}
      {list.data && (
        <div className="text-xs text-text-dim">
          {list.data.users.length} из {list.data.total}
        </div>
      )}
      {list.data?.users.map((u) => (
        <UserRow key={u.tgId} u={u} viewerIsSuperAdmin={viewerIsSuperAdmin} />
      ))}
    </div>
  );
}

function fmtCurrency(p: AdminPayment): string {
  if (p.currency === 'RUB') return `${p.amountValue}₽`;
  if (p.currency === 'XTR') return `${p.amountValue}⭐`;
  return `${p.amountValue} ${p.currency}`;
}

const STATUS_BADGES: Record<string, { label: string; color: string }> = {
  succeeded: { label: 'прошёл', color: 'var(--neon-green)' },
  pending: { label: 'ожидание', color: 'var(--neon-yellow)' },
  canceled: { label: 'отменён', color: 'var(--neon-red)' },
};

function PaymentRow({ p }: { p: AdminPayment }): JSX.Element {
  const status = STATUS_BADGES[p.status] ?? { label: p.status, color: 'var(--text-dim)' };

  return (
    <div className="flex items-start gap-3 rounded-lg border border-white/5 bg-bg-elev p-3">
      <UserAvatar tgId={p.tgId} username={p.username} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <div className="min-w-0 truncate text-sm font-medium">
            {p.username ? `@${p.username}` : <span className="text-text-dim">без username</span>}
          </div>
          <div className="shrink-0 font-semibold tabular-nums">{fmtCurrency(p)}</div>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-dim">
          <span className="font-mono">#{p.tgId}</span>
          <span aria-hidden>·</span>
          <span>{new Date(p.createdAt).toLocaleDateString('ru-RU')}</span>
          {p.isTest && (
            <span className="rounded bg-white/5 px-1 py-0.5 text-[10px] uppercase">test</span>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span
            className="rounded px-1.5 py-0.5 text-[10px] font-medium"
            style={{
              background: `color-mix(in srgb, ${status.color} 12%, transparent)`,
              color: status.color,
            }}
          >
            {status.label}
          </span>
          {p.receiptStatus === 'registered' && p.receiptUrl && (
            <button
              onClick={() => p.receiptUrl && openExternal(p.receiptUrl)}
              className="rounded px-1.5 py-0.5 text-[10px] font-medium"
              style={{ background: 'rgba(57,255,20,0.1)', color: 'var(--neon-green)' }}
            >
              чек ✓
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PaymentsSection(): JSX.Element {
  const q = useQuery<AdminPaymentsResponse>({
    queryKey: ['admin-payments'],
    queryFn: listAdminPayments,
  });

  return (
    <div className="space-y-2">
      <div className="text-xs text-text-dim">
        Архив платежей. Приём оплаты отключён вместе с ботом.
      </div>
      {q.isLoading && <div className="text-sm text-text-dim">Загрузка…</div>}
      {q.isError && <div className="text-sm text-neon-red">{(q.error as Error).message}</div>}
      {q.data?.payments.map((p) => (
        <PaymentRow key={p.id} p={p} />
      ))}
      {q.data?.payments.length === 0 && (
        <div className="text-sm text-text-dim">Платежей нет</div>
      )}
    </div>
  );
}

export function AdminTab(): JSX.Element {
  const me = useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/v1/me'),
  });
  const [section, setSection] = useState<Section>('debug');

  if (me.data && !me.data.isAdmin) {
    return <div className="p-6 text-center text-sm text-text-dim">Доступно только админу</div>;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="px-4 pt-4 lg:px-6 lg:pt-6">
        <div className="flex rounded-md border border-white/5 bg-bg-elev p-1 text-xs">
          <button
            onClick={() => setSection('debug')}
            className={`flex-1 rounded px-3 py-1.5 transition ${
              section === 'debug' ? 'bg-accent text-bg' : 'text-text-dim'
            }`}
          >
            🔧 Debug
          </button>
          <button
            onClick={() => setSection('users')}
            className={`flex-1 rounded px-3 py-1.5 transition ${
              section === 'users' ? 'bg-accent text-bg' : 'text-text-dim'
            }`}
          >
            👥 Юзеры
          </button>
          <button
            onClick={() => setSection('payments')}
            className={`flex-1 rounded px-3 py-1.5 transition ${
              section === 'payments' ? 'bg-accent text-bg' : 'text-text-dim'
            }`}
          >
            💳 Платежи
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 lg:px-6 lg:pb-6">
        {section === 'debug' && <DebugSection />}
        {section === 'users' && (
          <UsersSection viewerIsSuperAdmin={!!me.data?.isSuperAdmin} />
        )}
        {section === 'payments' && <PaymentsSection />}
      </div>
    </div>
  );
}
